const openClass = 'sidebar-open';
const openingClass = 'sidebar-opening';
const closedClass = 'sidebar-closed';
const closingClass = 'sidebar-closing';
const openCollapsibleClass = 'collapsible-open';
const activeItemClass = 'active';
const transitionDuration = 200;
const collapsiblesStorageKey = 'econ-sidebar-collapsibles';
const openStorageKey = 'econ-sidebar-open';

function uniqueOnly(value, index, array) {
  return array.indexOf(value) === index;
}

function removeFromArray(value, array) {
  const i = array.indexOf(value);
  if (i !== -1) {
    array.splice(i, 1);
  }

  return array;
}

// OPTIONS
// trackCollapsibles: will write the state of collapsibles to local storage and restore it on page load

class Sidebar {
  constructor(sidebarEl, options) {
    this.sidebarEl = sidebarEl;
    this.outsideToggle = document.querySelector('.sidebar-outside-switch');
    this.innerToggle = sidebarEl.querySelector('.sidebar-switch');
    this.collapsibles = [...sidebarEl.querySelectorAll('.sidebar-collapsible')];
    this.sidebarLinks = [...sidebarEl.querySelectorAll('.sidebar-content a[href]')];
    this.trackCollapsibles = options.trackCollapsibles;

    this.openCollapsibles = this.collapsibles.reduce((acc, el, index) => {
      if (el.classList.contains(openCollapsibleClass)) {
        return [...acc, index];
      }

      return acc;
    }, []);

    this.isOpen = this.determineOpenInitially();
    this.addEventListeners();
    this.findActiveCollapsible();
    this.setActiveItems();
    this.onOpenChange();

    if (this.trackCollapsibles) {
      this.handleStoredSidebarState();
    }
  }

  get open() {
    return this.isOpen;
  }

  set open(value) {
    if (this.isOpen !== value) {
      this.isOpen = value;
      this.onOpenChange();
    }
  }

  isMobileView() {
    const toggleStyle = window.getComputedStyle(this.outsideToggle);

    return toggleStyle.display !== 'none';
  }

  determineOpenInitially() {
    if (this.trackCollapsibles && localStorage.getItem(openStorageKey) != null) {
      return JSON.parse(localStorage.getItem(openStorageKey));
    }

    if (this.sidebarEl.classList.contains(closedClass)) {
      return false;
    }

    if (this.sidebarEl.classList.contains(openClass)) {
      return true;
    }

    // If neither open nor closed classes are set, sidebar will be considered closed on mobile devices and open on desktop
    return !this.isMobileView();
  }

  addEventListeners() {
    this.innerToggle.addEventListener('click', () => this.toggleOpen());
    this.outsideToggle.addEventListener('click', () => this.toggleOpen());
    this.collapsibles.forEach((collapsible, index) => {
      const btn = collapsible.children[0];
      if (btn && btn.tagName === 'BUTTON') {
        btn.addEventListener('click', () => this.toggleCollapsibleOpen(collapsible, index));
      }
    });

    // I have not found a reliable native way to listen to navigation changes, therefore using a custom event
    document.addEventListener('econ-sidebar-navigated', (e) => {
      this.setActiveItems(e.detail?.path);
      if (this.isMobileView()) {
        this.open = false;
      }
    });
  }

  findActiveCollapsible() {
    this.collapsibles.forEach((collapsible) => {
      const children = [...collapsible.children].find(child => child.tagName === 'UL');
      if (children) {
        const active = children.querySelector('.active');
        if (active) {
          collapsible.classList.add(openCollapsibleClass);
        }
      }
    });
  }

  setActiveItems(path = window.location.pathname) {
    this.collapsibles.forEach((collapsible) => {
      collapsible.classList.remove(activeItemClass);
    });

    this.sidebarLinks.forEach((link) => {
      if (link.getAttribute('href') === path) {
        link.classList.add(activeItemClass);
        const collapsible = link.closest('.sidebar-collapsible');
        if (collapsible) {
          collapsible.classList.add(activeItemClass);
        }
      } else {
        link.classList.remove(activeItemClass);
      }
    });
  }

  onOpenChange() {
    const open = this.isOpen;
    const handleTempClass = (className) => {
      this.sidebarEl.classList.add(className);
      setTimeout(() => this.sidebarEl.classList.remove(className), transitionDuration);
    };

    if (open) {
      handleTempClass(openingClass);
      this.sidebarEl.classList.remove(closedClass);
      this.sidebarEl.classList.add(openClass);
    } else {
      handleTempClass(closingClass);
      this.sidebarEl.classList.remove(openClass);
      this.sidebarEl.classList.add(closedClass);
    }
  }

  toggleOpen() {
    this.open = !this.open;
  }

  toggleCollapsibleOpen(collapsible, index) {
    const collapsibleOpen = collapsible.classList.contains(openCollapsibleClass);

    if (!this.isOpen) {
      this.isOpen = true;
      setTimeout(() => {
        collapsible.classList.add(openCollapsibleClass);
      }, transitionDuration);

      return;
    }

    if (collapsibleOpen) {
      collapsible.classList.remove(openCollapsibleClass);
      this.openCollapsibles = removeFromArray(index, this.openCollapsibles);
    } else {
      collapsible.classList.add(openCollapsibleClass);
      this.openCollapsibles = [...this.openCollapsibles, index].filter(uniqueOnly);
    }
  }

  handleStoredSidebarState() {
    window.addEventListener('beforeunload', () => this.writeSidebarStateToLocalStorage());
    this.openCollapsibles = JSON.parse(localStorage.getItem(collapsiblesStorageKey)) || [];
    this.openCollapsibles.forEach((index) => {
      this.collapsibles[index].classList.add(openCollapsibleClass);
    });
  }

  writeSidebarStateToLocalStorage() {
    localStorage.setItem(collapsiblesStorageKey, JSON.stringify(this.openCollapsibles));
    localStorage.setItem(openStorageKey, JSON.stringify(this.open));
  }
}

export const initSidebar = (options = {}) => {
  const sidebarEl = document.querySelector('.sidebar');
  if (!sidebarEl) {
    return null;
  }

  return new Sidebar(sidebarEl, options);
};
